import type {
  ManualCommandResult,
  ManualControlReleaseResult,
  ManualControlSession,
  ManualOperatingTarget,
} from '@/types'

const BASE_URL = '/manual-operating-api'

function token(): string | null {
  return localStorage.getItem('auth_token')
}

function newIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }
  // The development integration dashboard is currently served over plain
  // LAN HTTP, where Web Crypto randomUUID may be unavailable. This value is
  // only a request de-duplication key, not a credential.
  return `manual-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const authToken = token()
  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(options.headers as Record<string, string> | undefined),
    },
  })
  if (!response.ok) {
    let message = `HTTP ${response.status}`
    try {
      const body = await response.json() as { detail?: unknown }
      if (body.detail) {
        if (typeof body.detail === 'string') message = body.detail
        else if (
          typeof body.detail === 'object'
          && body.detail !== null
          && 'message' in body.detail
          && typeof body.detail.message === 'string'
        ) message = body.detail.message
        else message = JSON.stringify(body.detail)
      }
    } catch {
      // Response body is optional.
    }
    throw { status: response.status, message }
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export function listManualOperatingTargets(): Promise<ManualOperatingTarget[]> {
  return request<{ targets: ManualOperatingTarget[] }>('/targets')
    .then((response) => response.targets)
}

export function getManualOperatingTarget(targetId: string): Promise<ManualOperatingTarget> {
  return request(`/targets/${encodeURIComponent(targetId)}`)
}

export function createManualControlSession(targetId: string): Promise<ManualControlSession> {
  return request(`/targets/${encodeURIComponent(targetId)}/control-sessions`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export function createReconnectTicket(
  sessionId: string,
  reconnectToken: string,
): Promise<{ ticket: string }> {
  return request(`/control-sessions/${encodeURIComponent(sessionId)}/reconnect-ticket`, {
    method: 'POST',
    body: JSON.stringify({ reconnect_token: reconnectToken }),
  })
}

export function releaseManualControlSession(
  sessionId: string,
  reconnectToken: string,
  options: { keepalive?: boolean } = {},
): Promise<ManualControlReleaseResult> {
  return request<ManualControlReleaseResult>(`/control-sessions/${encodeURIComponent(sessionId)}/release`, {
    method: 'POST',
    keepalive: options.keepalive,
    body: JSON.stringify({ reconnect_token: reconnectToken }),
  })
}

export function setManualOperatingMode(
  targetId: string,
  mode: 'manual' | 'auto',
  sessionId: string,
): Promise<ManualCommandResult> {
  return request(`/targets/${encodeURIComponent(targetId)}/mode`, {
    method: 'POST',
    body: JSON.stringify({
      mode,
      session_id: sessionId,
      idempotency_key: newIdempotencyKey(),
    }),
  })
}

export function sendManualAction(
  targetId: string,
  actionKey: string,
  sessionId: string,
): Promise<ManualCommandResult> {
  return request(`/targets/${encodeURIComponent(targetId)}/commands`, {
    method: 'POST',
    body: JSON.stringify({
      action_key: actionKey,
      session_id: sessionId,
      idempotency_key: newIdempotencyKey(),
    }),
  })
}

export function controlWebSocketUrl(sessionId: string, ticket: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}${BASE_URL}/control-sessions/`
    + `${encodeURIComponent(sessionId)}/ws?ticket=${encodeURIComponent(ticket)}`
}
